import React from 'react';
import { Drawer, Descriptions, Tag, Spin } from 'antd';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  GET_AB_ADDRESS_MAPPING_BY_ID,
  GetAbAddressMappingByIdResponse,
} from '../graphql/abAddressGraphql';

interface AbAddressDetailDrawerProps {
  open: boolean;
  id: string | null;
  onClose: () => void;
}

const reviewStatusTag = (status: number, t: (k: string) => string) => {
  switch (status) {
    case 1:
      return <Tag color="green">{t('wms.abAddress.status.approved')}</Tag>;
    case 2:
      return <Tag color="red">{t('wms.abAddress.status.rejected')}</Tag>;
    case 0:
    default:
      return <Tag color="orange">{t('wms.abAddress.status.pending')}</Tag>;
  }
};

const AbAddressDetailDrawer: React.FC<AbAddressDetailDrawerProps> = ({ open, id, onClose }) => {
  const { t } = useTranslation();

  const { data, loading } = useQuery<GetAbAddressMappingByIdResponse>(
    GET_AB_ADDRESS_MAPPING_BY_ID,
    {
      variables: { id: id ? Number(id) : undefined },
      skip: !open || !id,
      fetchPolicy: 'cache-and-network',
    }
  );

  const record = data?.getAbAddressMappingById;
  const dash = '—';
  const fmt = (v?: string | null) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : dash);

  return (
    <Drawer
      title={t('wms.abAddress.detailTitle')}
      open={open}
      onClose={onClose}
      width={640}
      destroyOnClose
    >
      <Spin spinning={loading}>
        {record && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label={t('wms.abAddress.table.id')}>
              {record.id ?? dash}
            </Descriptions.Item>
            <Descriptions.Item label={t('wms.abAddress.table.aAddress')}>
              {record.aFullAddress ?? dash}
            </Descriptions.Item>
            <Descriptions.Item label={t('wms.abAddress.table.bAddress')}>
              {record.bFullAddress ?? dash}
            </Descriptions.Item>
            <Descriptions.Item label={t('wms.abAddress.form.reason')}>
              {record.reason ?? dash}
            </Descriptions.Item>
            <Descriptions.Item label={t('wms.abAddress.table.reporter')}>
              {record.reporterName ?? record.reporterAccountId ?? dash}
            </Descriptions.Item>
            <Descriptions.Item label={t('wms.abAddress.table.status')}>
              {reviewStatusTag(record.reviewStatus, t)}
            </Descriptions.Item>
            <Descriptions.Item label={t('wms.abAddress.detail.reviewNote')}>
              {record.reviewNote ?? dash}
            </Descriptions.Item>
            <Descriptions.Item label={t('wms.abAddress.detail.reviewedAt')}>
              {fmt(record.reviewedAt)}
            </Descriptions.Item>
            <Descriptions.Item label={t('wms.abAddress.table.createdAt')}>
              {fmt(record.createdAt)}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Spin>
    </Drawer>
  );
};

export default AbAddressDetailDrawer;
